// api
import { EAllowStatus, ResourceActive } from '~/src/dash/api/console-api';
import {
  parseArray,
  parseBoolTrue,
  parseNumber,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import {
  addDomainAndTokenToUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';
import { EBool } from '~/src/dash/api/types/bool';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
// utils
import { getPbsUS, getResellerTitle } from '~/src/dash/resellers/utils/importResellerConfig';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { EPadderType } from '~/src/dash/views/networks/network-settings-view-constants';

// |------------------ NETWORK ENUMS ------------------|
// NOTE:

export enum ENetworkRole {
  ROLE_UNKNOWN = 0,
  ROLE_OWNER,
  ROLE_READ,
}

export enum NetworkStreamType {
  TYPE_UNKNOWN = 0,
  TYPE_LIVE,
  TYPE_VOD,
  TYPE_DVR,
  TYPE_FOD,
}

export enum NetworkDemandSourceType {
  TYPE_UNKNOWN = 0,
  TYPE_VAST,
  TYPE_PUBLICA,
  TYPE_ROUTE,
}

// |------------------ NETWORK CONSTS ------------------|

export const NETWORK_AD_URL_SITE_PREFIX = getPbsUS() + '/v1/s2s-hb?';

interface INetworkParams {
  [key: string]: string;
}

export const NETWORK_VOD_PARAMS: INetworkParams = {
  ip: '[IP]',
  ua: '[UA]',
  preroll: '[PREROLL]',
  ad_breaks: '[AD_BREAKS]',
  avod: '[AVOD]',
};

export const NETWORK_LIVE_PARAMS: INetworkParams = {
  ip: '[IP]',
  ua: '[UA]',
  pod_duration: '[POD_DURATION]',
};

export const NETWORK_LIVE_PARAMS_REQUIRED_TYPE: INetworkParams = {
  app_bundle: '[APP_BUNDLE]',
  site_page: '[SITE_PAGE]',
};

export const NETWORK_VOD_PARAMS_REQUIRED_TYPE: INetworkParams = {
  app_bundle: '[APP_BUNDLE]',
  site_page: '[SITE_PAGE]',
};

export const NETWORK_VOD_PARAMS_REQUIRED_ABDREAK_TYPE: INetworkParams = {
  slot_count: '[SLOT_COUNT]',
  pod_duration: '[POD_DURATION]',
};

export const NETWORK_NOT_EDITABLE_TYPE: INetworkParams = {
  network_id: '',
  min_ad_duration: '',
  max_ad_duration: '',
};

export const AllNetworkStreamTypes = [NetworkStreamType.TYPE_LIVE, NetworkStreamType.TYPE_VOD];

interface INetworkRoleMap {
  [key: number]: ENetworkRole;
}
export const NETWORK_ROLE_MAP: INetworkRoleMap = {
  0: ENetworkRole.ROLE_UNKNOWN,
  1: ENetworkRole.ROLE_OWNER,
  2: ENetworkRole.ROLE_READ,
};

export type NetworkTrackerEventType = number;
export const NetworkTrackerEventTypeStart: NetworkTrackerEventType = 4000;
export const NetworkTrackerEventTypePodRequest: NetworkTrackerEventType = 4001;
export const NetworkTrackerEventTypePodFilled: NetworkTrackerEventType = 4002;
export const NetworkTrackerEventTypePodComplete: NetworkTrackerEventType = 4003;
export const NetworkTrackerEventTypePodAdFilled: NetworkTrackerEventType = 4004;
export const NetworkTrackerEventTypePodAdImpression: NetworkTrackerEventType = 4005;
export const NetworkTrackerEventTypePodAdComplete: NetworkTrackerEventType = 4006;
export const NetworkTrackerEventTypePodAdVodWin: NetworkTrackerEventType = 4024;

interface ITrackerEventType {
  Name: string;
  Value: number;
}

export const NetworkTrackerEventTypes: ITrackerEventType[] = [
  {
    Name: 'Start',
    Value: NetworkTrackerEventTypeStart,
  },
  {
    Name: 'Pod Request',
    Value: NetworkTrackerEventTypePodRequest,
  },
  {
    Name: 'Pod Filled',
    Value: NetworkTrackerEventTypePodFilled,
  },
  {
    Name: 'Pod Complete',
    Value: NetworkTrackerEventTypePodComplete,
  },
  {
    Name: 'Ad Filled',
    Value: NetworkTrackerEventTypePodAdFilled,
  },
  {
    Name: 'Ad Impression',
    Value: NetworkTrackerEventTypePodAdImpression,
  },
  {
    Name: 'Ad Complete',
    Value: NetworkTrackerEventTypePodAdComplete,
  },
  {
    Name: 'Ad Vod Win',
    Value: NetworkTrackerEventTypePodAdVodWin,
  },
];

export type NetworkInVideoUnitPartner = number;
export const NetworkInVideoUnitPartnerUnknown: NetworkInVideoUnitPartner = 0;
export const NetworkInVideoUnitPartnerTriplelift: NetworkTrackerEventType = 1;
export const NetworkInVideoUnitPartnerGumgum: NetworkTrackerEventType = 2;

export const NetworkInVideoUnitPartners: ITrackerEventType[] = [
  {
    Name: 'Triplelift',
    Value: NetworkInVideoUnitPartnerTriplelift,
  },
  {
    Name: 'Gumgum',
    Value: NetworkInVideoUnitPartnerGumgum,
  },
];

// |------------------ NETWORK UTIL FUNCTIONS ------------------|
export function networkRoleToString(role: ENetworkRole) {
  switch (role) {
    case ENetworkRole.ROLE_OWNER:
      return 'Owner';
    case ENetworkRole.ROLE_READ:
      return 'Read';
    default:
      return 'Unknown';
  }
  return 'Unknown';
}

export function networkStreamTypeToString(stream_type: NetworkStreamType) {
  switch (stream_type) {
    case NetworkStreamType.TYPE_LIVE:
      return 'Live';
    case NetworkStreamType.TYPE_VOD:
      return 'VOD';
    case NetworkStreamType.TYPE_DVR:
      return 'DVR';
    case NetworkStreamType.TYPE_FOD:
      return 'FOD';
    default:
      break;
  }
  return 'unknown';
}

export function streamTypeStringToNetworkStream(stream_type: string): NetworkStreamType {
  switch (stream_type) {
    case 'Live':
      return NetworkStreamType.TYPE_LIVE;
    case 'VOD':
      return NetworkStreamType.TYPE_VOD;
    case 'DVR':
      return NetworkStreamType.TYPE_DVR;
    case 'FOD':
      return NetworkStreamType.TYPE_FOD;
    default:
      break;
  }
  return NetworkStreamType.TYPE_UNKNOWN;
}

export function networkDemandSourceTypeToString(source_type: NetworkDemandSourceType) {
  switch (source_type) {
    case NetworkDemandSourceType.TYPE_PUBLICA:
      return getResellerTitle();
    case NetworkDemandSourceType.TYPE_VAST:
      return 'VAST';
    case NetworkDemandSourceType.TYPE_ROUTE:
      return 'Route';
    default:
      break;
  }
  return 'unknown';
}

export function demandSourceStringToDemandSourceType(source_type: string): NetworkDemandSourceType {
  switch (source_type) {
    case getResellerTitle():
      return NetworkDemandSourceType.TYPE_PUBLICA;
    case 'VAST':
      return NetworkDemandSourceType.TYPE_VAST;
    case 'Route':
      return NetworkDemandSourceType.TYPE_ROUTE;
    default:
      break;
  }
  return NetworkDemandSourceType.TYPE_UNKNOWN;
}

export const networkRoleNumberToRole = (role: number): ENetworkRole => {
  return NETWORK_ROLE_MAP[role] || ENetworkRole.ROLE_UNKNOWN;
};

export const buildNetworkChannelList = (
  currentNetwork: INetwork,
  selectedIDs: string[],
  defaultSelectAll: boolean,
): IMenuList => {
  const list = {} as IMenuList;

  currentNetwork.Channels.forEach((networkChannel: INetworkChannel) => {
    list[networkChannel.ID] = {
      title: networkChannel.Name,
      ID: networkChannel.ID.toString(),
      selected: defaultSelectAll ? true : selectedIDs.includes(networkChannel.ID.toString()),
    } as IListItem;
  });
  return list;
};

// |------------------ NETWORK INTERFACES and TYPES ------------------|
// NOTE: For typing, we should try to match typing defined in Go's models
// For example, if an ID is a number, it should be a number in JS or if
// there is an enum in Go, we should create an enum here as well.

// NOTE: naming should match case (PascalCase) and spelling in Go (models.go) and the JSON
// tag that is used when the object is marshalled to JSON (models.go and proto).
// This will reduce potentially typo errors and make it easier to find the fields.
// Capitalized fields can also help indicate that this field is part of the model in the DB.

// NOTE: Match the fields in models.go and use the same order so it is easy to check and make sure
// we have all the same fields as platform since they must match completely to update correctly

export interface IParam {
  name: string;
  value: string;
}

export interface ISSAIHouseAd {
  ID: number;
  PublisherID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  Name: string;
  URL: string;
  Width: number;
  Height: number;
  Bitrate: number;
  Duration: number;
  MimeType: string;
  TranscodeStatus: string;
}

export interface ISSAIPadderAd {
  ID: number;
  PublisherID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  Name: string;
  URL: string;
  Width: number;
  Height: number;
  Bitrate: number;
  Duration: number;
  MimeType: string;
  TranscodeStatus: string;
  PadderType: EPadderType;
}

export interface INetworkTracker {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  Active: ResourceActive;
  EventType: NetworkTrackerEventType;
  URL: string;
  NetworkID: number;
}

export interface INetworkChannel {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  NetworkID: number;
  Name: string;
  AdURL: string;
  AppBundleOverride: string;
  Weight: number;
  SiteID: number | undefined;
  RouteID: number | undefined;
  DemandSourceType: NetworkDemandSourceType;
}

export type NetworkChannelField =
  | 'Name'
  | 'AdURL'
  | 'AppBundleOverride'
  | 'Weight'
  | 'DemandSourceType'
  | 'SiteID'
  | 'RouteID';

export interface INetwork {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  Name: string;
  UUID: string;
  StreamURL: string;
  Channels: INetworkChannel[];
  NetworkTrackers: INetworkTracker[];
  NetworkPublisherMaps: INetworkPublisherMap[];
  NetworkInVideoUnits: INetworkInVideoUnit[];
  EndpointURLParams: string;
  StreamTimeout: number;
  InventoryTimeout: number;
  PodDuration: number;
  DDMonitoring: boolean;
  RealtimeVOD: boolean;
  RealtimeVODSlate: string;
  Active: number;
  StreamType: NetworkStreamType;
  SlateURL: string;
  TargetingSCTECueIDs: string;
  TargetingSCTECueIDsIsAllowed: EAllowStatus;
  NormalizeContentValues: EBool;
  AllowSilentAds: EBool;
  EnableBrightlineIntegration: EBool;
  AudioNormalizeTarget: number;
  EnableAudioNormalization: EBool;
  EnableTranscoderV3: EBool;
  EnableJingle: EBool;
  EnableBumper: EBool;
  HouseAds: ISSAIHouseAd[];
  PadderAds: ISSAIPadderAd[];
  EnableLiveContext: EBool;
  LiveContextStreamID: string;
  LiveContextLookbackMinutes: number;
  EnableRenderRateGuard: EBool;
  PulseDetectionPeriod: number;
}

export interface INetworkPublisherMap {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  PublisherID: number;
  NetworkID: number;
  NetworkPublisherRole: ENetworkRole;
}

export interface INetworkInVideoUnit {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  Active: ResourceActive;
  Partner: NetworkInVideoUnitPartner;
  URL: string;
  NetworkID: number;
}

// |------------------ NETWORK TRANSFORM FUNCTIONS ------------------|
// NOTE: The string should match the the name of the field/json tag from models.go
// So if we are using PascaleCase there, we ONLY need Pascale case here

// NOTE: Match the fields in models.go and use the same order so it is easy to check and make sure
// we have all the same fields as platform since they must match completely to update correctly
export const transformNetworkTracker = transformModel<INetworkTracker>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Active: parseNumber(['Active']),
  EventType: parseNumber(['EventType']),
  URL: parseString(['URL']),
  NetworkID: parseNumber(['NetworkID']),
});

export const transformNetworkChannel = transformModel<INetworkChannel>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  NetworkID: parseNumber(['NetworkID']),
  Name: parseString(['Name']),
  AdURL: parseString(['AdURL']),
  AppBundleOverride: parseString(['AppBundleOverride']),
  Weight: parseNumber(['Weight']),
  SiteID: parseNumber(['SiteID']),
  RouteID: parseNumber(['RouteID']),
  DemandSourceType: parseNumber(['DemandSourceType']),
});

export const transformNetworkPadderAds = transformModel<ISSAIPadderAd>({
  ID: parseNumber(['ID']),
  PublisherID: parseNumber(['PublisherID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',
  Name: parseString(['Name']),
  URL: parseString(['URL']),
  Width: parseNumber(['Width']),
  Height: parseNumber(['Height']),
  Bitrate: parseNumber(['Bitrate']),
  Duration: parseNumber(['Duration']),
  MimeType: parseString(['MimeType']),
  TranscodeStatus: parseString(['TranscodeStatus']),
  PadderType: parseString(['PadderType']),
});

export const transformNetworkHouseAds = transformModel<ISSAIHouseAd>({
  ID: parseNumber(['ID']),
  PublisherID: parseNumber(['PublisherID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',
  Name: parseString(['Name']),
  URL: parseString(['URL']),
  Width: parseNumber(['Width']),
  Height: parseNumber(['Height']),
  Bitrate: parseNumber(['Bitrate']),
  Duration: parseNumber(['Duration']),
  MimeType: parseString(['MimeType']),
  TranscodeStatus: parseString(['TranscodeStatus']),
});

export const transformNetworkTrackerWrapper = (fromObj: any) => {
  return transformNetworkTracker(fromObj);
};

export const transformNetworkChannelWrapper = (fromObj: any) => {
  // convert siteID (nullint64 in Go)
  // @ts-ignore
  if ('SiteID' in fromObj && !!fromObj.SiteID && typeof fromObj.SiteID !== 'number') {
    // @ts-ignore
    fromObj.SiteID = fromObj.SiteID.Int64;
  }
  return transformNetworkChannel(fromObj);
};

export const transformNetworkHouseAdsWrapper = (fromObj: any) => {
  return transformNetworkHouseAds(fromObj);
};

export const transformNetworkPadderAdsWrapper = (fromObj: any) => {
  return transformNetworkPadderAds(fromObj);
};

export const transformNetworkPublisherMap = transformModel<INetworkPublisherMap>({
  ID: parseNumber(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  PublisherID: parseNumber(['PublisherID']),
  NetworkID: parseNumber(['NetworkID']),
  NetworkPublisherRole: parseNumber(['NetworkPublisherRole']),
});

export const transformNetworkPublisherMapWrapper = (fromObj: any): INetworkPublisherMap => {
  return transformNetworkPublisherMap(fromObj);
};

export const transformNetworkInVideoUnit = transformModel<INetworkInVideoUnit>({
  ID: parseNumber(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Active: parseNumber(['Active']),
  Partner: parseNumber(['Partner']),
  URL: parseString(['URL']),
  NetworkID: parseNumber(['NetworkID']),
});

export const transformNetworkInVideoUnitWrapper = (fromObj: any): INetworkInVideoUnit => {
  return transformNetworkInVideoUnit(fromObj);
};

// NOTE: Match the fields in models and order so it is easy to spot since we want to make sure
// we have all the fields as platform
export const transformNetwork = transformModel<INetwork>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Name: parseString(['Name']),
  UUID: parseString(['UUID']),
  StreamURL: parseString(['StreamURL']),
  Channels: parseArray(['Channels'], transformNetworkChannelWrapper),
  NetworkTrackers: parseArray(['NetworkTrackers'], transformNetworkTrackerWrapper),
  NetworkPublisherMaps: parseArray(['NetworkPublisherMaps'], transformNetworkPublisherMapWrapper),
  NetworkInVideoUnits: parseArray(['NetworkInVideoUnits'], transformNetworkInVideoUnitWrapper),
  EndpointURLParams: parseString(['EndpointURLParams']),
  StreamTimeout: parseNumber(['StreamTimeout']),
  InventoryTimeout: parseNumber(['InventoryTimeout']),
  AudioNormalizeTarget: 'AudioNormalizeTarget',
  PodDuration: parseNumber(['PodDuration']),
  DDMonitoring: parseBoolTrue(['DDMonitoring']),
  RealtimeVOD: parseBoolTrue(['RealtimeVOD']),
  RealtimeVODSlate: parseString(['RealtimeVODSlate']),
  Active: parseNumber(['Active']),
  StreamType: parseNumber(['StreamType']),
  SlateURL: parseString(['SlateURL']),
  TargetingSCTECueIDs: parseString(['TargetingSCTECueIDs']),
  TargetingSCTECueIDsIsAllowed: parseNumber('TargetingSCTECueIDsIsAllowed'),
  NormalizeContentValues: parseNumber('NormalizeContentValues', EBool.EBool_FALSE),
  AllowSilentAds: parseNumber('AllowSilentAds', EBool.EBool_FALSE),
  EnableBrightlineIntegration: parseNumber('EnableBrightlineIntegration', EBool.EBool_FALSE),
  EnableAudioNormalization: parseNumber('EnableAudioNormalization', EBool.EBool_FALSE),
  EnableJingle: parseNumber('EnableJingle', EBool.EBool_FALSE),
  EnableBumper: parseNumber('EnableBumper', EBool.EBool_FALSE),
  EnableTranscoderV3: parseNumber('EnableTranscoderV3', EBool.EBool_FALSE),
  HouseAds: parseArray(['HouseAds'], transformNetworkHouseAdsWrapper),
  PadderAds: parseArray(['PadderAds'], transformNetworkPadderAdsWrapper),
  EnableLiveContext: parseNumber(['EnableLiveContext']),
  LiveContextStreamID: parseString(['LiveContextStreamID']),
  LiveContextLookbackMinutes: parseNumber(['LiveContextLookbackMinutes'], 2),
  EnableRenderRateGuard: parseNumber(['EnableRenderRateGuard']),
  PulseDetectionPeriod: parseNumber(['PulseDetectionPeriod']),
});

export const transformNetworkWrapper = (fromObj: any) => {
  return transformNetwork(fromObj);
};

// |------------------ NETWORK API CALLS ------------------|

// NOTE: API calls should follow the following naming convention
// ids should be made a part of the path
// resource should be pluralized (networks)
// there should be no trailing "/"

// GET all resources for this publisher
// /publishers/{publisherID}/resource_name

// GET, PATCH, DELETE a single resource
// /publishers/{publisherID}/resource_name/{resourceID}

// GET, PATCH, DELETE a single resource that is nested
// /publishers/{publisherID}/resource_name/{resourceID}/nested_resource_name/{nested_resource_id}

// POST a single resource
// /publishers/{publisherID}/resource_name

// POST a single resource that is nested
// /publishers/{publisherID}/resource_name/nested_resource_name

export async function getNetwork(
  networkID: string,
  publisherID: string,
): Promise<IHttpResponse<INetwork>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/networks/${networkID}`);
  return await restGet<INetwork>(reqUrl, {});
}

export async function getNetworks(publisherID: string): Promise<IHttpResponse<INetwork[]>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/networks`);
  return await restGet<INetwork[]>(reqUrl, {});
}

export async function createNetwork(
  network: INetwork,
  publisherID: string,
): Promise<IHttpResponse<INetwork>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/networks`);
  const parsedNetwork = transformNetwork(network);
  return await restPost<INetwork>(reqUrl, parsedNetwork);
}

// update network will execute a full or partial update of the resource.  A full update will include all fields,
// child associations and will overwrite this data.  A partial update will only update the fields given through
// the network param

// NOTE: Because a full object is required, make sure that all fields in the object are defined in the interface/transform
export async function updateNetwork(
  network: INetwork,
  publisherID: string,
  fullUpdate: boolean,
): Promise<IHttpResponse<INetwork>> {
  const partialUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/networks/${network.ID}`);
  const reqUrl = fullUpdate ? partialUrl + '&full_update=true' : partialUrl;
  const parsedNetwork = transformNetwork(network);
  return await restPatch<INetwork>(reqUrl, fullUpdate ? parsedNetwork : network);
}

export async function deleteNetwork(
  networkID: string,
  publisherID: string,
): Promise<IHttpResponse<INetwork>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/networks/${networkID}`);
  return await restDelete<INetwork>(reqUrl, undefined);
}

// Network Tracker operations
export async function deleteNetworkTracker(
  trackerID: string,
  networkID: string,
  publisherID: string,
): Promise<IHttpResponse<INetworkTracker>> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/networks/${networkID}/network_trackers/${trackerID}`,
  );
  return await restDelete<INetworkTracker>(reqUrl, undefined);
}

// Network In Video Unit operations
export async function deleteNetworkInVideoUnit(
  id: string,
  networkID: string,
  publisherID: string,
): Promise<IHttpResponse<INetworkInVideoUnit>> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/networks/${networkID}/network_in_video_units/${id}`,
  );
  return await restDelete<INetworkInVideoUnit>(reqUrl, undefined);
}

// Network Publisher Map operations
export async function deleteNetworkPublisherMap(
  id: string,
  networkID: string,
  publisherID: string,
): Promise<IHttpResponse<INetworkPublisherMap>> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/networks/${networkID}/network_publisher_maps/${id}`,
  );
  return await restDelete<INetworkPublisherMap>(reqUrl, undefined);
}

// Network Channel operations
export async function deleteNetworkChannel(
  id: string,
  networkID: string,
  publisherID: string,
): Promise<IHttpResponse<INetworkChannel>> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/networks/${networkID}/network_channels/${id}`,
  );
  return await restDelete<INetworkChannel>(reqUrl, undefined);
}

// Network House Ad operations
export async function getSSAIHouseAdsByPublisherID(
  publisherID: string,
): Promise<IHttpResponse<ISSAIHouseAd[]>> {
  const reqUrl =
    addDomainAndTokenToUrl(`/publisher/ssai_house_ad`) + `&publisher_id=${publisherID}`;
  return await restGet<ISSAIHouseAd[]>(reqUrl, undefined);
}

export async function uploadSSAIHouseAdByURL(
  publisherID: string,
  url: string,
  name: string,
): Promise<IHttpResponse<ISSAIHouseAd[]>> {
  const reqUrl =
    addDomainAndTokenToUrl(`/ssai_house_ad`) +
    `&publisher_id=${publisherID}&url=${url}&name=${name}`;
  return await restPost<ISSAIHouseAd[]>(reqUrl, undefined);
}

export async function getNetworksMenuList(
  session: ISessionState,
): Promise<Record<string, IMenuList>> {
  return await getNetworks(session.publisher.ID)
    .then((val: IHttpResponse<any>) => {
      const networks = val.parsedBody?.map(transformNetwork);
      return { networks: generateNetworkMenuList(networks) };
    })
    .catch((e) => {
      console.error(e);
      return {
        networks: {},
      };
    });
}

const generateNetworkMenuList = (networks: INetwork[]): IMenuList => {
  const menuList = {} as IMenuList;
  networks.forEach((network) => {
    menuList[network.ID] = {
      ID: String(network.ID),
      title: network.Name,
      selected: false,
    };
  });
  return menuList;
};
